import { useCallback, useState } from 'react';
import algoliaInstance from '@source/services/Algolia';
import { ALGOLIA_INDEXES_COUNTRIES } from '@source/constants/algolia';
import useRegionConfig from '@source/hooks/useRegionConfig';
import Logger from '@source/services/Logger';
import { TLanguageKey, TRegionKey } from '@source/interface';
import { NEXT_PUBLIC_WEB_CR } from '@source/constants/env';
import { lowerCase, startCase } from 'lodash';

// Popular brands by region (in display order)
export const POPULAR_BRANDS_BY_REGION: Record<string, string[]> = {
  sg: ['honda', 'bmw', 'toyota', 'mercedes-benz', 'hyundai', 'mazda', 'audi', 'kia', 'nissan'],
  my: ['perodua', 'honda', 'proton', 'toyota', 'nissan', 'mazda', 'mitsubishi', 'mercedes-benz', 'volkswagen'],
  id: ['toyota', 'honda', 'daihatsu', 'suzuki', 'mitsubishi', 'hyundai', 'nissan', 'wuling', 'mazda'],
  th: ['honda', 'bmw', 'toyota', 'mercedes-benz', 'hyundai', 'mazda', 'audi', 'kia', 'nissan'],
  jp: ['toyota', 'tesla', 'honda', 'suzuki', 'nissan', 'mazda', 'hyundai', 'bmw', 'subaru'],
  hk: ['tesla', 'mercedes-benz', 'bmw', 'toyota', 'honda', 'audi', 'mazda', 'mini', 'nissan'],
};

export interface MakeItem {
  name: string;
  count: number;
}

export interface BrandItem {
  id: string;
  label: string;
  slug: string;
  url: string;
}

export interface DropdownCategories {
  popular: {
    label: string;
    columns: BrandItem[][];
  };
  moreBrands: {
    label: string;
    columns: BrandItem[][];
  };
}

const buildBrandUrl = (region: TRegionKey, lang: TLanguageKey, brandSlug: string): string => {
  return `${NEXT_PUBLIC_WEB_CR}/${region}/${lang}/buy/${brandSlug}`;
};

const makeToSlug = (make: string): string => {
  return make.toLowerCase().replace(/\s+/g, '-');
};

// Split into columns left to right (horizontal distribution)
const splitIntoColumnsHorizontal = (items: BrandItem[], numColumns: number): BrandItem[][] => {
  const columns: BrandItem[][] = Array.from({ length: numColumns }, () => []);
  items.forEach((item, index) => {
    columns[index % numColumns].push(item);
  });
  return columns;
};

// Split into columns top to bottom (vertical distribution)
const splitIntoColumnsVertical = (items: BrandItem[], numColumns: number): BrandItem[][] => {
  const columns: BrandItem[][] = Array.from({ length: numColumns }, () => []);
  const itemsPerColumn = Math.ceil(items.length / numColumns);

  items.forEach((item, index) => {
    const columnIndex = Math.floor(index / itemsPerColumn);
    columns[Math.min(columnIndex, numColumns - 1)].push(item);
  });

  return columns;
};

const useBrandsServices = () => {
  const [makes, setMakes] = useState<MakeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    countryConfig: { country: countryCode },
  } = useRegionConfig();

  const fetchMakes = useCallback(async () => {
    if (!countryCode) return [];

    setLoading(true);
    setError(null);

    try {
      const indexes = ALGOLIA_INDEXES_COUNTRIES[countryCode as keyof typeof ALGOLIA_INDEXES_COUNTRIES];
      const indexName = indexes?.latest?.asc;

      if (!indexName) {
        throw new Error(`No Algolia index found for country: ${countryCode}`);
      }

      const result = await algoliaInstance.search(indexName, '', {
        hitsPerPage: 0,
        facets: ['inventory.make'],
      });

      const makeFacets = result?.facets?.['inventory.make'] || {};
      const makesList = Object.entries(makeFacets).map(([name, count]) => ({
        name,
        count: count as number,
      }));

      // Sort by count descending
      makesList.sort((a, b) => b.count - a.count);

      setMakes(makesList);
      return makesList;
    } catch (err) {
      Logger.log('Error fetching makes:', err);
      setError(err as Error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  const getDropdownCategories = useCallback(
    (makesList: MakeItem[], lang: TLanguageKey, device: 'mobile' | 'desktop' = 'desktop'): DropdownCategories => {
      const region = countryCode as TRegionKey;
      const numColumns = device === 'mobile' ? 2 : 3;
      const popularCount = 9;

      // Convert makes to brand items (already sorted by count from fetchMakes)
      const allBrands: BrandItem[] = makesList.map((make) => {
        const slug = makeToSlug(make.name);
        return {
          id: slug,
          label: startCase(lowerCase(make.name)),
          slug,
          url: buildBrandUrl(region, lang, slug),
        };
      });

      // Split into popular (top 9 by count) and more brands (sorted alphabetically)
      const popularBrands = allBrands.slice(0, popularCount);
      const moreBrands = allBrands.slice(popularCount).sort((a, b) => a.label.localeCompare(b.label));

      return {
        popular: {
          label: 'shared.topNavigation.buyDropdown.popular',
          columns: splitIntoColumnsHorizontal(popularBrands, numColumns),
        },
        moreBrands: {
          label: 'shared.topNavigation.buyDropdown.moreBrands',
          columns: splitIntoColumnsVertical(moreBrands, numColumns),
        },
      };
    },
    [countryCode],
  );

  return {
    makes,
    loading,
    error,
    fetchMakes,
    getDropdownCategories,
  };
};

export default useBrandsServices;
